import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { usePipedriveStore, useTenantInMarketStore } from "../..";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import { formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import FieldRow from "../FieldRow";

function TenantInMarketDetailsDetailsTab() {
    const { id } = useParams();
    const tenantInMarketStore = useTenantInMarketStore();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarket = useMemo(() => computed(() => tenantInMarketStore.tenantsInMarket.find(tim => tim.id.toString() === id)!), [id]).get();
    const assignedTo = useMemo(() => computed(() => pipedriveStore.users.find(u => u.id === tenantInMarket.assignedToPipedriveUserId)), []).get();
    const companyPipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === tenantInMarket.pipedriveCompanyId)), [tenantInMarket]).get();
    const brokeragePipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === tenantInMarket.pipedriveBrokerageId)), [tenantInMarket]).get();
    const navigateToCompany = useNavigateToCompany();
    const bodyContainerClassName = useBodyContainerClassName();

    return (
        <div className={bodyContainerClassName}>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Details</div>
                    <FieldRow label="ASSIGNED TO">
                        {assignedTo && assignedTo.name}
                    </FieldRow>
                    <FieldRow label="TENANT">
                        {companyPipedriveOrganization && (
                            <span className={commonStyles.link} onClick={() => navigateToCompany(companyPipedriveOrganization.id)}>
                                {companyPipedriveOrganization.name}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="LAND AREA MIN">
                        {!!tenantInMarket.landAreaMin && `${formatNumber(tenantInMarket.landAreaMin)} Acres`}
                    </FieldRow>
                    <FieldRow label="LAND AREA MAX">
                        {!!tenantInMarket.landAreaMax && `${formatNumber(tenantInMarket.landAreaMax)} Acres`}
                    </FieldRow>
                    <FieldRow label="BUILDING AREA MIN">
                        {!!tenantInMarket.buildingAreaMin && `${formatNumber(tenantInMarket.buildingAreaMin)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="BUILDING AREA MAX">
                        {!!tenantInMarket.buildingAreaMax && `${formatNumber(tenantInMarket.buildingAreaMax)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="USE TYPE">
                        {tenantInMarket.useType}
                    </FieldRow>
                    <FieldRow label="CONTACT">
                        {tenantInMarket.contact}
                    </FieldRow>
                    <FieldRow label="BROKERAGE">
                        {brokeragePipedriveOrganization && (
                            <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToOrganization(brokeragePipedriveOrganization.id)}>
                                {brokeragePipedriveOrganization.name}
                            </a>
                        )}
                    </FieldRow>
                </div>
            </div>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>&nbsp;</div>
                    <FieldRow label="TARGET ANNUAL RENT">
                        {!!tenantInMarket.targetAnnualRent && formatPrice(tenantInMarket.targetAnnualRent)}
                    </FieldRow>
                    <FieldRow label="ZONING REQUIRED">
                        {tenantInMarket.zoningRequired}
                    </FieldRow>
                    <FieldRow label="SITE CONDITIONS">
                        {tenantInMarket.siteConditions}
                    </FieldRow>
                    <FieldRow label="TIMING">
                        {tenantInMarket.timing}
                    </FieldRow>
                    <FieldRow label="COMPANY WEBSITE">
                        {tenantInMarket.companyWebsite && (
                            <a className={commonStyles.link} href={tenantInMarket.companyWebsite} target="_blank">
                                {tenantInMarket.companyWebsite}
                            </a>
                        )}
                    </FieldRow>
                    <FieldRow label="CORP OWNERSHIP">
                        {tenantInMarket.corpOwnership}
                    </FieldRow>
                    <FieldRow label="REVENUE">
                        {tenantInMarket.revenue}
                    </FieldRow>
                    <FieldRow label="NUMBER OF EMPLOYEES">
                        {!!tenantInMarket.numberOfEmployees && formatNumber(tenantInMarket.numberOfEmployees)}
                    </FieldRow>
                    <FieldRow label="NUMBER OF LOCATIONS">
                        {!!tenantInMarket.numberOfLocations && formatNumber(tenantInMarket.numberOfLocations)}
                    </FieldRow>
                    <FieldRow label="NOTES">
                        {tenantInMarket.notes}
                    </FieldRow>
                    <FieldRow label="LAST MODIFIED">
                        <span>{tenantInMarket.modifiedByUser}</span>
                        <br />
                        <span>{formatDateTime(tenantInMarket.modifiedDate)}</span>
                    </FieldRow>
                    <FieldRow label="INACTIVE REASON">
                        {tenantInMarket.inactiveReason}
                    </FieldRow>
                </div>
            </div>
        </div>
    );
}

export default observer(TenantInMarketDetailsDetailsTab);
